import { createSlice } from "@reduxjs/toolkit";
import { GAME_LAYOUT } from "../../settings";
import StorageManager from "../../utils/StorageManager";

/**
 * Pot Collection Slice
 *
 * Dedicated state for the Pot Collection (Zeus Respins) Hold & Win bonus game.
 * Decoupled from featureGameSettingsSlice to prevent state bloat.
 *
 * Architecture: zeus_respins_architecture_analysis.md §10
 * GDD: §5.5–5.10, §13–15, §23
 */

// Zeus symbol IDs → feature letter mapping (for activation queue building)
const ZEUS_IDS_TO_FEATURE = {
  12: "D", 20: "D",               // cd, cd_mult
  13: "M", 21: "M", 22: "M", 23: "M", // cm, cm_minor/mini/major
  14: "C", 25: "C",               // cc, cc_empty
};
const FEATURE_ORDER = { M: 0, D: 1, C: 2 };

/**
 * Build activationQueue by scanning initialRespinScreen for Zeus coins.
 * Returns one entry per Zeus coin with position, ordered M→D→C.
 * This replaces the old respin_game_type parsing which deduplicated
 * same-type Zeus (e.g. "DM" for 2 Doubles + 1 Mystery → only 1 Double).
 */
function buildActivationQueue(initialRespinScreen) {
  if (!Array.isArray(initialRespinScreen)) return [];
  const queue = [];
  for (let ri = 0; ri < initialRespinScreen.length; ri++) {
    const reel = initialRespinScreen[ri];
    if (!Array.isArray(reel)) continue;
    for (let si = 0; si < reel.length; si++) {
      const feature = ZEUS_IDS_TO_FEATURE[reel[si]];
      if (feature) {
        queue.push({ feature, reelIndex: ri, symbolIndex: si });
      }
    }
  }
  queue.sort((a, b) => FEATURE_ORDER[a.feature] - FEATURE_ORDER[b.feature]);
  return queue;
}

/**
 * Hydrate PC slice from init/refresh featureState (cold load + error recovery).
 * No-op unless featureState.type === "bonus" — FR/base refresh must stay untouched.
 */
function hydratePotCollectionFromInit(state, featureState) {
  if (!featureState || featureState.type !== "bonus") return;

  const data = featureState.data;

  // Store feature data regardless of phase — it's needed when we enter bonus
  state.respinGameType = data.respinGameType || "";
  state.activationQueue = buildActivationQueue(data.initialRespinScreen);

  if (data.initialRespinScreen) {
    state.initialRespinScreen = data.initialRespinScreen;
  }
  if (data.initialRespinValues) {
    state.initialRespinValues = data.initialRespinValues;
  }

  state.respinsLeft = data.respinsLeft;
  state.respinsIssued = data.respinsIssued;

  if (data.currentRound) {
    state.currentRound = data.currentRound;
  }

  if (data.screenProgress) {
    state.screenProgress = data.screenProgress;
    state.screenProgressIndex = 0;
  }

  // Determine reload phase based on state
  if (featureState.phase === "intro") {
    // ── Intro Reload: bonus triggered but no respins played yet ──
    // Stay in BASE GAME view. The normal entry flow handles everything:
    //   base game reels (from localStorage) → win processing → intro popup → transition → bonus

    state.potCollectionActive = false;  // NOT in bonus scene yet
    state.potCollectionTriggered = true; // Signal: intro popup should show
    state.entryPhase = "popup";          // Start entry flow at popup phase
    state.reloadPhase = "intro";         // Distinct from mid-bonus "respins"

    // Coin count — start at Zeus coin count only (same as normal trigger).
    // The entry sequence replays spawnCoins which increments for each basic coin.
    // Using data.coinCount here would double-count since it's the FINAL total.
    state.coinCount = state.activationQueue.length;

  } else if (featureState.phase === "in_progress") {
    // ── Mid-bonus reload: already playing respins ──
    state.potCollectionActive = true;
    state.potCollectionTriggered = true;
    state.entryPhase = "ready";
    state.reloadPhase = "respins";

    // Strip wins from base game round so reload won't replay win animations
    StorageManager.stripBaseGameWins();

    // Server data passthrough — needed for coin value display
    state.valuesGrid = data.valuesGrid || data.initialRespinValues || null;
    state.originalValuesGrid = data.originalValues || data.initialRespinOriginalValues || null;
    state.prevScreen = data.prevScreen || null;

    // Override initialRespinScreen/Values with CURRENT board state.
    // features.initial_respin_* is always the bonus START state (never changes).
    // outcome.screen + storage.values reflect the board AFTER played respins.
    if (data.currentRound?.reels) {
      state.initialRespinScreen = data.currentRound.reels;
    }
    if (state.valuesGrid) {
      state.initialRespinValues = state.valuesGrid;
    }

    // Build flipped coin positions — mystery coins that were FLIPPED from
    // jackpot (j.json) to regular coin (c.json) by Double or Collect.
    //
    // PRIORITY: Use localStorage-persisted flippedCoinPositions if available.
    // The API's storage.events only contains the LATEST respin's events,
    // so intermediate c_collect/d_doubles events from prior respins are lost.
    // localStorage accumulates them across all respins during live play.
    const savedBonusRound = StorageManager.loadBonusGameRound();
    const savedFlipped = savedBonusRound?.flippedCoinPositions;

    if (Array.isArray(savedFlipped) && savedFlipped.length > 0) {
      // localStorage has persisted flipped positions — use them
      state.flippedCoinPositions = savedFlipped;
    } else {
      // Fallback: reconstruct from API events (works for trigger + latest round)
      const initialEvents = data.initialEvents || [];
      const storageEvents = data.currentRound?.storage?.events || data.currentRound?.storage?.screen_progress || [];

      const BLANK_ID = 16;
      const onBoard = new Set();
      const flippedPositions = new Set();

      const processEvents = (events) => {
        for (const event of events) {
          const eventType = event.type || event.action;
          const coords = event.coordinates;
          if (!Array.isArray(coords)) continue;
          const pairs = Array.isArray(coords[0]) ? coords : [coords];

          if (eventType === "new_coins" || eventType === "m_reveals") {
            for (const pair of pairs) {
              if (Array.isArray(pair) && pair.length >= 2) {
                onBoard.add(`${pair[0]}_${pair[1]}`);
              }
            }
          } else if (eventType === "d_doubles") {
            for (const pair of pairs) {
              if (Array.isArray(pair) && pair.length >= 2) {
                flippedPositions.add(`${pair[0]}_${pair[1]}`);
              }
            }
          } else if (eventType === "c_collect") {
            for (const key of onBoard) {
              flippedPositions.add(key);
            }
          }
        }
      };

      processEvents(initialEvents);

      const prevScreen = data.prevScreen || data.currentRound?.storage?.prev_screen || [];
      for (let ri = 0; ri < prevScreen.length; ri++) {
        if (!Array.isArray(prevScreen[ri])) continue;
        for (let si = 0; si < prevScreen[ri].length; si++) {
          const cellId = parseInt(prevScreen[ri][si], 10);
          if (!isNaN(cellId) && cellId !== BLANK_ID) {
            onBoard.add(`${ri}_${si}`);
          }
        }
      }

      processEvents(storageEvents);
      state.flippedCoinPositions = [...flippedPositions];
    }

    // Coin count — pre-calculated by RGS
    if (data.coinCount !== undefined) {
      state.coinCount = data.coinCount;
    }

    // Win limit flag — restore from localStorage (BE source of truth)
    if (savedBonusRound?.winLimitReached) {
      state.winLimitReached = true;
    }

    // Trigger win — read from dedicated trigger_win key (set at bonus trigger time)
    const triggerWin = StorageManager.loadTriggerWin();
    if (triggerWin > 0) {
      state.triggerRound = { win: triggerWin };
    }


  } else if (featureState.phase === "completion") {
    // ── Completion Reload: bonus finished, replay collection from localStorage ──
    state.potCollectionActive = true;
    state.potCollectionTriggered = true;
    state.entryPhase = "ready"; // SpinEngine needs "ready" to create, then we trigger EOB
    state.reloadPhase = "collection";

    // Strip wins from base game round so reload won't replay win animations
    StorageManager.stripBaseGameWins();

    // Grid state — use final board from localStorage
    state.valuesGrid = data.valuesGrid || data.initialRespinValues || null;
    state.originalValuesGrid = data.originalValues || data.initialRespinOriginalValues || null;
    state.prevScreen = data.prevScreen || null;

    // Use the final board state (last respin's outcome)
    if (data.currentRound?.reels) {
      state.initialRespinScreen = data.currentRound.reels;
    }
    if (state.valuesGrid) {
      state.initialRespinValues = state.valuesGrid;
    }

    // ALL coins are regular (flipped) — bonus is done, everything was collected
    // Build flipped positions from ALL non-blank cells on the final board
    const BLANK_ID = 16;
    const finalScreen = data.currentRound?.reels || data.initialRespinScreen || [];
    const allPositions = [];
    for (let ri = 0; ri < finalScreen.length; ri++) {
      if (!Array.isArray(finalScreen[ri])) continue;
      for (let si = 0; si < finalScreen[ri].length; si++) {
        const cellId = parseInt(finalScreen[ri][si], 10);
        if (!isNaN(cellId) && cellId !== BLANK_ID) {
          allPositions.push(`${ri}_${si}`);
        }
      }
    }
    state.flippedCoinPositions = allPositions;

    // Coin count — server won't provide for closed rounds, use localStorage
    if (data.coinCount !== undefined) {
      state.coinCount = data.coinCount;
    } else {
      const savedBonusForCount = StorageManager.loadBonusGameRound();
      if (savedBonusForCount?.coinCount !== undefined) {
        state.coinCount = savedBonusForCount.coinCount;
      }
    }

    // Win limit flag — restore from localStorage (BE source of truth)
    const savedBonusForWinLimit = StorageManager.loadBonusGameRound();
    if (savedBonusForWinLimit?.winLimitReached) {
      state.winLimitReached = true;
    }
  }

  // Component readiness — they will report when mounted
  state.symbolsReady = false;
  state.reelsReady = false;
  state.overlayReelsReady = false;
}

const initialState = {
  // === Feature Lifecycle ===
  potCollectionActive: false,     // true when bonus scene is active
  potCollectionTriggered: false,  // true when trigger detected, before transition

  // === Entry Flow Phase Tracking ===
  // Tracks which phase of the bonus entry sequence we're in.
  // Phases progress linearly: none → popup → transition → ejection → activation → feature → ready
  entryPhase: "none", // 'none' | 'popup' | 'transition' | 'ejection' | 'activation' | 'feature' | 'ready'
  ejectionComplete: false,        // true after all coins ejected from orb
  activationQueue: [],            // ordered list of features to activate: ['M','D','C']
  currentActivation: null,        // which feature is currently activating
  featureApplicationComplete: false, // true after all features applied

  // === Respin Counter (RC pattern: optimistic decrement + pending apply) ===
  respinsLeft: 0,
  pendingRespinsLeft: null,       // API value, applied when first coin lands
  respinsIssued: 0,               // Total respins issued at bonus start (always 3 per GDD §5.5)

  // === Grid Data ===
  initialRespinScreen: [],        // [row][reel] from BE — bonus starting grid
  initialRespinValues: [],        // [row][reel] from BE — pre-feature coin values
  triggerRound: null,             // Original trigger round data (for win-during-trigger handling)
  currentRound: {
    reels: [],
    wins: [],
    win: 0,
    storage: null,                // screen_progress, coin values, etc.
  },

  // === Server Data (source of truth for respins) ===
  prevScreen: null,               // Pre-feature grid from storage.prev_screen
  valuesGrid: null,               // Post-feature coin values from storage.values
  originalValuesGrid: null,       // Pre-feature drawn values from storage.original_values (before doubling/collect)
  newCoins: [],                   // Computed by RGS.js: new coins that landed this spin

  // === screen_progress ===
  screenProgress: [],             // current screen_progress array from BE
  screenProgressIndex: 0,         // which action we're currently processing
  screenProgressProcessing: false, // true while processing an action

  // === POT Tracking ===
  respinGameType: "",             // "D"/"M"/"C"/"DM"/"DC"/"MC"/"DMC"

  // === Coin Counter ===
  coinCount: 0,                   // Current count (N/15)

  // === Component Readiness (RC pattern) ===
  // Each component group reports ready independently.
  // Total counts: 5 reels × 3 symbols = 15 symbols
  symbolsReady: false,
  readySymbolsCount: 0,
  reelsReady: false,
  readyReelsCount: 0,
  overlayReelsReady: false,
  readyOverlayReelsCount: 0,

  // === Reload Recovery (GDD §23.2 — 4 phases) ===
  reloadPhase: "none", // 'none' | 'respins' | 'feature_application' | 'collection' | 'total_win'
  // 'respins' → show Reload popup with remaining respins, resume last ongoing respin
  // 'feature_application' → no popup, re-present feature application
  // 'collection' → no popup, re-display Total Win Collection
  // 'total_win' → no popup, display Total Win popup

  // === Win Limit (Spec v2 §outcome_storage) ===
  winLimitReached: false,          // true when ×10,000 max win cap hit — force end-of-bonus

  // === Mystery Flip Tracking (reload recovery) ===
  // Set of "reel_row" keys for mystery coins that were FLIPPED from jackpot (j.json)
  // to regular coin (c.json) by either Double (d_doubles) or Collect (c_collect).
  // m_reveals only means "mystery activated → shuffled → became jackpot" — NOT flipped.
  // Unflipped mysteries stay as j.json with tier skin, no value text.
  flippedCoinPositions: [],    // Array of "r_row" strings e.g. ["1_0", "4_0"]
};

const TOTAL_SYMBOLS = GAME_LAYOUT.REEL_COUNT * GAME_LAYOUT.SYMBOLS_VISIBLE; // 5×3 = 15
const TOTAL_REELS = GAME_LAYOUT.REEL_COUNT; // 5

const potCollectionSlice = createSlice({
  name: "potCollection",
  initialState,
  reducers: {
    // ── Feature Lifecycle ──
    setPotCollectionTriggered: (state, action) => {
      state.potCollectionTriggered = action.payload;
    },
    setPotCollectionActive: (state, action) => {
      state.potCollectionActive = action.payload;
      // Reset pending respins when deactivating
      if (!action.payload) {
        state.pendingRespinsLeft = null;
      }
    },

    // ── Entry Phase ──
    setEntryPhase: (state, action) => {
      state.entryPhase = action.payload;
    },
    setEjectionComplete: (state, action) => {
      state.ejectionComplete = action.payload;
    },
    setActivationQueue: (state, action) => {
      state.activationQueue = action.payload;
    },
    setCurrentActivation: (state, action) => {
      state.currentActivation = action.payload;
    },
    setFeatureApplicationComplete: (state, action) => {
      state.featureApplicationComplete = action.payload;
    },

    // ── Respin Counter ──
    setRespinsLeft: (state, action) => {
      state.respinsLeft = action.payload;
    },
    setPendingRespinsLeft: (state, action) => {
      state.pendingRespinsLeft = action.payload;
    },
    setRespinsIssued: (state, action) => {
      state.respinsIssued = action.payload;
    },
    decrementRespins: (state) => {
      if (state.respinsLeft > 0) {
        state.respinsLeft -= 1;
      }
    },
    resetRespins: (state) => {
      state.respinsLeft = 3; // GDD §5.5: always 3
    },
    // Apply pending respins from API (RC pattern: first coin landing applies)
    applyPendingRespins: (state) => {
      if (state.pendingRespinsLeft !== null) {
        state.respinsLeft = state.pendingRespinsLeft;
        state.pendingRespinsLeft = null;
      } else {
      }
    },

    // ── Grid Data ──
    setInitialRespinScreen: (state, action) => {
      state.initialRespinScreen = action.payload;
    },
    setInitialRespinValues: (state, action) => {
      state.initialRespinValues = action.payload;
    },
    setTriggerRound: (state, action) => {
      state.triggerRound = action.payload;
    },
    setCurrentRound: (state, action) => {
      state.currentRound = action.payload;
    },
    setRespinGameType: (state, action) => {
      state.respinGameType = action.payload;
    },

    // ── screen_progress ──
    setScreenProgress: (state, action) => {
      state.screenProgress = action.payload;
      state.screenProgressIndex = 0;
      state.screenProgressProcessing = false;
    },
    advanceScreenProgress: (state) => {
      state.screenProgressIndex += 1;
      state.screenProgressProcessing = false;
    },
    setScreenProgressProcessing: (state, action) => {
      state.screenProgressProcessing = action.payload;
    },

    // ── Coin Counter ──
    setCoinCount: (state, action) => {
      state.coinCount = action.payload;
    },
    incrementCoinCount: (state) => {
      state.coinCount += 1;
    },
    // Clear stale newCoins at spin start — prevents _computeTensionCell from
    // reading previous spin's newCoins before the current API responds.
    clearNewCoins: (state) => {
      state.newCoins = [];
    },

    // ── Component Readiness (RC pattern) ──
    reportSymbolReady: (state) => {
      state.readySymbolsCount += 1;
      if (state.readySymbolsCount >= TOTAL_SYMBOLS) {
        state.symbolsReady = true;
      }
    },
    reportReelReady: (state) => {
      state.readyReelsCount += 1;
      if (state.readyReelsCount >= TOTAL_REELS) {
        state.reelsReady = true;
      }
    },
    reportOverlayReelReady: (state) => {
      state.readyOverlayReelsCount += 1;
      if (state.readyOverlayReelsCount >= TOTAL_REELS) {
        state.overlayReelsReady = true;
      }
    },
    resetReadiness: (state) => {
      state.symbolsReady = false;
      state.readySymbolsCount = 0;
      state.reelsReady = false;
      state.readyReelsCount = 0;
      state.overlayReelsReady = false;
      state.readyOverlayReelsCount = 0;
    },

    // ── Reload Recovery ──
    setReloadPhase: (state, action) => {
      state.reloadPhase = action.payload;
    },

    // ── Full Reset (bonus ended → return to base game) ──
    // Preserves currentRound so WinDisplay can keep showing "Total Win: X"
    // after the outro transition. Cleared by clearPotCollectionWin on next spin.
    resetPotCollection: (state) => {
      const preservedRound = state.currentRound;
      Object.assign(state, initialState);
      state.currentRound = preservedRound;
    },

    // ── Clear win data (next base game spin) ──
    clearPotCollectionWin: (state) => {
      state.currentRound = initialState.currentRound;
    },
  },
  extraReducers: (builder) => {
    // Import rgsPlay / init / refresh inline to avoid circular dependency
    const { rgsPlay } = require("./spinSlice");
    const { initializeGame, refreshGameData } = require("./gameSettingsSlice");

    builder
      // NOTE: PC does NOT decrement in rgsPlay.pending (unlike H&W).
      // RC pattern: decrement happens in rgsPlay.fulfilled, after API confirms the spin.
      // This prevents the counter from dropping before the spin animation even starts.
      .addCase(rgsPlay.pending, (state) => {
        // No-op for PC — decrement is in fulfilled (RC pattern)
      })
      .addCase(rgsPlay.fulfilled, (state, action) => {
        const { featureState, round } = action.payload;
        if (!featureState || featureState.type !== "bonus") return;

        if (featureState.phase === "intro") {
          // ── Trigger Detection ──
          state.potCollectionTriggered = true;
          state.entryPhase = "popup";

          state.respinGameType = featureState.data.respinGameType || "";

          // Build activationQueue from grid — one entry per Zeus coin.
          // respin_game_type deduplicates (e.g. "DM" for 2D+1M) so we scan the grid.
          state.activationQueue = buildActivationQueue(featureState.data.initialRespinScreen);

          // Grid data
          if (featureState.data.initialRespinScreen) {
            state.initialRespinScreen = featureState.data.initialRespinScreen;
          }
          if (featureState.data.initialRespinValues) {
            state.initialRespinValues = featureState.data.initialRespinValues;
          }
          if (featureState.data.initialRespinOriginalValues) {
            state.originalValuesGrid = featureState.data.initialRespinOriginalValues;
          }

          // screen_progress from trigger
          if (featureState.data.screenProgress) {
            state.screenProgress = featureState.data.screenProgress;
            state.screenProgressIndex = 0;
          }

          // Respins
          if (featureState.data.respinsLeft !== undefined) {
            state.respinsLeft = featureState.data.respinsLeft;
            state.respinsIssued = featureState.data.respinsIssued ?? 3;
            state.pendingRespinsLeft = null;
          }

          // Store trigger round (for win-during-trigger handling, GDD §12.3)
          if (round) {
            state.triggerRound = round;
          }

          // Store current round
          if (featureState.data.currentRound) {
            state.currentRound = featureState.data.currentRound;
          }

          // Zeus coins are already on grid — start counter at their count.
          // Count from activationQueue (built from initialRespinScreen above).
          // Ejection phase will increment for each basic coin revealed.
          state.coinCount = state.activationQueue.length;

          // ── Build initial flippedCoinPositions from trigger screenProgress ──
          // During intro, screenProgress includes initial_events (new_coins, m_reveals,
          // c_collect, d_doubles). Process them to know which coins are already flipped.
          if (state.screenProgress && state.screenProgress.length > 0) {
            const onBoard = new Set();
            const flipped = new Set();
            for (const event of state.screenProgress) {
              const eventType = event.action || event.type;
              const coords = event.coordinates;
              if (!Array.isArray(coords)) continue;
              const pairs = Array.isArray(coords[0]) ? coords : [coords];
              if (eventType === 'new_coins' || eventType === 'm_reveals') {
                for (const p of pairs) { if (Array.isArray(p) && p.length >= 2) onBoard.add(`${p[0]}_${p[1]}`); }
              } else if (eventType === 'd_doubles') {
                for (const p of pairs) { if (Array.isArray(p) && p.length >= 2) flipped.add(`${p[0]}_${p[1]}`); }
              } else if (eventType === 'c_collect') {
                for (const key of onBoard) flipped.add(key);
              }
            }
            state.flippedCoinPositions = [...flipped];
          }

        } else if (featureState.phase === "in_progress") {
          // ── Active Respin Response ──
          if (featureState.data.currentRound) {
            state.currentRound = featureState.data.currentRound;
          }

          // screen_progress for this respin
          if (featureState.data.screenProgress) {
            state.screenProgress = featureState.data.screenProgress;
            state.screenProgressIndex = 0;
            state.screenProgressProcessing = false;
          }

          // ── Server data passthrough (source of truth) ──
          state.prevScreen = featureState.data.prevScreen || null;
          state.valuesGrid = featureState.data.valuesGrid || null;
          state.originalValuesGrid = featureState.data.originalValues || null;
          state.newCoins = featureState.data.newCoins || [];

          // Store pending respins value for SpinEngine to read on first coin landing.
          // No decrement here — counter visual is driven imperatively by SpinEngine.
          if (featureState.data.respinsLeft !== undefined) {
            state.pendingRespinsLeft = featureState.data.respinsLeft;
          }

          // Win limit flag (Spec v2: ×10,000 max win cap)
          state.winLimitReached = !!featureState.data.winLimitReached;

          // ── Accumulate flippedCoinPositions from this respin's events ──
          // screenProgress has the CURRENT round's events. We merge any new
          // d_doubles or c_collect flips into the existing accumulated set.
          if (state.screenProgress && state.screenProgress.length > 0) {
            const existingFlipped = new Set(state.flippedCoinPositions || []);
            // Build onBoard from prevScreen (all coins before this spin) + new_coins
            const onBoard = new Set();
            const BLANK_ID = 16;
            const ps = state.prevScreen || [];
            for (let ri = 0; ri < ps.length; ri++) {
              if (!Array.isArray(ps[ri])) continue;
              for (let si = 0; si < ps[ri].length; si++) {
                const cellId = parseInt(ps[ri][si], 10);
                if (!isNaN(cellId) && cellId !== BLANK_ID) {
                  onBoard.add(`${ri}_${si}`);
                }
              }
            }
            for (const event of state.screenProgress) {
              const eventType = event.action || event.type;
              const coords = event.coordinates;
              if (!Array.isArray(coords)) continue;
              const pairs = Array.isArray(coords[0]) ? coords : [coords];
              if (eventType === 'new_coins' || eventType === 'm_reveals') {
                for (const p of pairs) { if (Array.isArray(p) && p.length >= 2) onBoard.add(`${p[0]}_${p[1]}`); }
              } else if (eventType === 'd_doubles') {
                for (const p of pairs) { if (Array.isArray(p) && p.length >= 2) existingFlipped.add(`${p[0]}_${p[1]}`); }
              } else if (eventType === 'c_collect') {
                for (const key of onBoard) existingFlipped.add(key);
              }
            }
            state.flippedCoinPositions = [...existingFlipped];
          }

        }
      })

      // ── Reload Recovery: Restore PC state from init / error-recovery refresh ──
      .addCase(initializeGame.fulfilled, (state, action) => {
        hydratePotCollectionFromInit(state, action.payload?.result?.featureState);
      })
      // Same hydrate as cold load — ErrorRecovery uses refreshGameData, not initializeGame.
      // Intro + mid only: completion/EOB needs bonus LS and cannot be rebuilt from init alone.
      // Early-returns when not bonus so FR/base 205 recovery is unchanged.
      .addCase(refreshGameData.fulfilled, (state, action) => {
        const featureState = action.payload?.result?.featureState;
        if (featureState?.type === "bonus" && featureState.phase === "completion") {
          return;
        }
        hydratePotCollectionFromInit(state, featureState);
      });
  },
});

export const {
  setPotCollectionTriggered,
  setPotCollectionActive,
  setEntryPhase,
  setEjectionComplete,
  setActivationQueue,
  setCurrentActivation,
  setFeatureApplicationComplete,
  setRespinsLeft,
  setPendingRespinsLeft,
  setRespinsIssued,
  decrementRespins,
  resetRespins,
  applyPendingRespins,
  setInitialRespinScreen,
  setInitialRespinValues,
  setTriggerRound,
  setCurrentRound,
  setRespinGameType,
  setScreenProgress,
  advanceScreenProgress,
  setScreenProgressProcessing,
  setCoinCount,
  incrementCoinCount,
  clearNewCoins,
  reportSymbolReady,
  reportReelReady,
  reportOverlayReelReady,
  resetReadiness,
  setReloadPhase,
  resetPotCollection,
  clearPotCollectionWin,
} = potCollectionSlice.actions;

export default potCollectionSlice.reducer;
